// Task vocabulary shared by the portfolio Tasks page, the per-job task view and
// the detail panel.
//
// The statuses beyond not-started/in-progress/done exist because these are
// compliance tasks: each of Blocked, Awaiting Permit and Awaiting Parts tells
// the dispatcher what specifically needs unblocking.

import { TONES } from '../shared/StatusChip';
import { TZ } from './dates';

const tone = (t) => ({ bg: TONES[t].bg, fg: TONES[t].fg, border: TONES[t].border, mark: TONES[t].mark });

// Colours come from the shared brand tones; every status also carries an icon so
// it is never shown by colour alone.
export const TASK_STATUSES = [
  { key: 'not_started', label: 'Not started', icon: 'fa-regular fa-circle', ...tone('neutral'), board: true },
  { key: 'in_progress', label: 'In progress', icon: 'fa-solid fa-circle-half-stroke', ...tone('info'), board: true },
  { key: 'blocked', label: 'Blocked', icon: 'fa-solid fa-ban', ...tone('danger'), board: true },
  { key: 'awaiting_permit', label: 'Awaiting permit', icon: 'fa-solid fa-file-shield', ...tone('warning'), board: false },
  { key: 'awaiting_parts', label: 'Awaiting parts', icon: 'fa-solid fa-box-open', ...tone('warning'), board: false },
  { key: 'done', label: 'Done', icon: 'fa-solid fa-circle-check', ...tone('success'), board: true },
];
export const STATUS_INDEX = TASK_STATUSES.reduce((a, s) => { a[s.key] = s; return a; }, {});

/**
 * Status metadata that never returns undefined: a status the server knows but this
 * build doesn't (a custom one, or a typo in imported data) renders as a neutral chip
 * with its own label instead of crashing the board.
 */
export const statusMetaFor = (key) => {
  if (STATUS_INDEX[key]) return STATUS_INDEX[key];
  const raw = String(key ?? '').replace(/_/g, ' ').trim();
  return { key: key || 'unknown', label: raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Unknown', icon: 'fa-regular fa-circle-question', ...tone('muted'), board: false };
};

// Board columns — the transient waiting states collapse into Blocked so the
// board stays four columns wide and readable.
export const BOARD_COLUMNS = ['not_started', 'in_progress', 'blocked', 'done'];
export const boardColumnFor = (t) => {
  if (t.status === 'awaiting_permit' || t.status === 'awaiting_parts') return 'blocked';
  // Unknown statuses land in "Not started" rather than vanishing from the board.
  return BOARD_COLUMNS.includes(t.status) ? t.status : 'not_started';
};

export const TASK_TYPES = [
  { key: 'site_visit', label: 'Site visit', icon: 'fa-solid fa-truck' },
  { key: 'report', label: 'Report', icon: 'fa-solid fa-file-lines' },
  { key: 'quote', label: 'Quote', icon: 'fa-solid fa-file-invoice' },
  { key: 'follow_up', label: 'Follow-up', icon: 'fa-solid fa-rotate-right' },
  { key: 'remedial', label: 'Remedial action', icon: 'fa-solid fa-screwdriver-wrench' },
  { key: 'admin', label: 'Admin', icon: 'fa-solid fa-clipboard' },
];
export const TYPE_INDEX = TASK_TYPES.reduce((a, t) => { a[t.key] = t; return a; }, {});

export const TASK_SOURCES = [
  { key: 'manual', label: 'Created manually' },
  { key: 'finding', label: 'From a finding' },
  { key: 'recurring', label: 'From a schedule' },
  { key: 'template', label: 'From a template' },
];

export const SAFETY_MODULES = [
  { key: 'fire', label: 'Fire' },
  { key: 'roof', label: 'Roof / height safety' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'water', label: 'Water / legionella' },
  { key: 'gas', label: 'Gas' },
  { key: 'lifting', label: 'Lifting equipment' },
  { key: 'general', label: 'General' },
];

export const VIEWS = [
  { key: 'list', label: 'List', icon: 'fa-solid fa-list' },
  { key: 'board', label: 'Board', icon: 'fa-solid fa-columns' },
  { key: 'calendar', label: 'Calendar', icon: 'fa-solid fa-calendar-days' },
  { key: 'timeline', label: 'Timeline', icon: 'fa-solid fa-bars-staggered' },
];

/**
 * The single most important derived value on the page: what this task is
 * screaming about. Ordered by how much it should worry the dispatcher, so a
 * row shows its worst problem rather than a pile of badges.
 */
export function taskFlags(t) {
  const out = [];
  if (t.sla_breached) out.push({ key: 'sla_breached', label: 'SLA breached', ...tone('danger') });
  else if (t.sla_at_risk) out.push({ key: 'sla_at_risk', label: 'SLA at risk', ...tone('warning') });
  if (t.awaiting_permit) out.push({ key: 'permit', label: 'Permit missing', ...tone('warning') });
  if (t.overdue) out.push({ key: 'overdue', label: 'Overdue', ...tone('danger') });
  else if (t.due_soon) out.push({ key: 'due_soon', label: 'Due soon', ...tone('warning') });
  return out;
}

const shortFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: '2-digit', month: 'short' });
/** "01 Oct" in London time. Date-only strings are read as calendar dates, never shifted by UTC. */
export const fmtDate = (d) => {
  if (!d) return '—';
  const m = typeof d === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(d.trim()) : null;
  const dt = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12)) : new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : shortFmt.format(dt);
};

export const initialsOf = (name) => String(name || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?';
